import { useState, useEffect, useCallback, useMemo } from 'react'
import { FileDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Formik, Form } from 'formik'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { toast } from 'sonner'

import GameroomScenarioSettingsSkeleton from '@/components/scenario/GameroomScenarioSettingsSkeleton'
import { GameRoomStatus } from '@/redux/types/room'
import RoomNotFound from '@/components/PageNotFound/RoomNotFound'
import BehaviorSection from '@/components/settings/BehaviorSection'
import FileUpload from '@/components/settings/FileUpload'
import { RootState } from '@/redux/store'
import { useAppDispatch } from '@/redux/hooks'
import { setUploadedFiles } from '@/redux/slices/configurationSlice'
import { getRoomDetails } from '@/redux/asyncThunks/room'
import {
  ChatbotType,
  DocumentRAGSettings,
} from '@/redux/types/configuration'
import { ImportModal } from '@/components/Room/ImportModal'
import {
  fetchRoomAdvisorConfig,
  saveRoomAdvisorConfig,
  RoomAdvisorConfig as RoomAdvisorConfigType,
} from '@/api/roomAdvisorConfig'
import { attachDocumentSettingsToFiles } from '@/utils/documentSettingsMapper'

const ROOM_FILE_RAG_DEFAULTS = {
  ragEnabled: true,
  ragTopK: 5,
  ragChunkSize: 1000,
  ragChunkOverlap: 200,
  ragSimilarityThreshold: 0.2,
  ragMaxContextLength: 3000,
}

export default function RoomAdvisorSettings() {
  const dispatch = useAppDispatch()
  const params = useParams()
  const roomId = params.roomId as string

  const { currentRoom, isLoading, error } = useSelector(
    (state: RootState) => state.room
  )
  const uploadedFiles = useSelector(
    (state: RootState) => state.configuration.files
  )

  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED

  const [roomAdvisorConfig, setRoomAdvisorConfig] =
    useState<RoomAdvisorConfigType | null>(null)
  const [isSavingRoomConfig, setIsSavingRoomConfig] = useState<boolean>(false)
  const [importWorldOpen, setImportWorldOpen] = useState(false)

  const applyRoomAdvisorConfig = useCallback(
    (config: RoomAdvisorConfigType | null) => {
      setRoomAdvisorConfig(config)
      if (config) {
        dispatch(
          setUploadedFiles(
            attachDocumentSettingsToFiles(
              config.files || [],
              config.document_settings
            )
          )
        )
      } else {
        dispatch(setUploadedFiles([]))
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (roomId) {
      dispatch(getRoomDetails(roomId))
    }
  }, [dispatch, roomId])

  useEffect(() => {
    const loadRoomAdvisorConfig = async () => {
      if (!roomId) return
      try {
        const config = await fetchRoomAdvisorConfig(roomId)
        applyRoomAdvisorConfig(config)
      } catch (error) {
        console.error('Failed to fetch room advisor config', error)
      }
    }
    loadRoomAdvisorConfig()
  }, [roomId, applyRoomAdvisorConfig])

  const roomConfigInitialValues = useMemo(
    () => ({
      greetings: roomAdvisorConfig?.greetings || '',
      prompt: roomAdvisorConfig?.prompt || '',
      scenario: roomAdvisorConfig?.scenario || '',
      restrictions: roomAdvisorConfig?.restrictions || '',
      currency_cost:
        roomAdvisorConfig?.currency_cost !== undefined &&
        roomAdvisorConfig?.currency_cost !== null
          ? roomAdvisorConfig.currency_cost
          : 0,
    }),
    [roomAdvisorConfig]
  )

  const handleSaveRoomAdvisorConfig = useCallback(
    async (values: typeof roomConfigInitialValues) => {
      if (isGameCompleted || !roomId) return
      setIsSavingRoomConfig(true)
      try {
        const documentSettings: Record<string, DocumentRAGSettings> = {}
        uploadedFiles.forEach((file) => {
          if (file.rag_settings) {
            documentSettings[file.id.toString()] = file.rag_settings
          }
        })

        const payload = {
          greetings: values.greetings,
          prompt: values.prompt,
          scenario: values.scenario,
          restrictions: values.restrictions,
          currency_cost: values.currency_cost,
          document_settings_data:
            Object.keys(documentSettings).length > 0
              ? documentSettings
              : undefined,
        }
        const updated = await saveRoomAdvisorConfig(roomId, payload)
        applyRoomAdvisorConfig(updated)
        toast.success('Room default advisor configuration saved successfully!')
      } catch (error: any) {
        toast.error(
          `Error saving room advisor configuration: ${
            error?.message || 'Unknown error'
          }`
        )
      } finally {
        setIsSavingRoomConfig(false)
      }
    },
    [isGameCompleted, roomId, uploadedFiles, applyRoomAdvisorConfig]
  )

  if (isLoading) {
    return <GameroomScenarioSettingsSkeleton />
  }

  if (error) {
    return (
      <div className='flex items-center justify-center w-full h-full text-center'>
        <RoomNotFound message='Unable to fetch the details.' />
      </div>
    )
  }

  if (!currentRoom) {
    return <div>No room details found.</div>
  }

  return (
    <TooltipProvider>
      <div className='relative px-24 py-6 space-y-6'>
        <div className='flex flex-wrap gap-x-2 gap-y-2 items-center justify-between'>
          <h1 className='text-2xl font-bold text-dark-blue'>
            World Setup
          </h1>
          {!isGameCompleted && (
            <Button
              type='button'
              variant='outline'
              className='border-dark-blue text-dark-blue hover:bg-slate-50'
              onClick={() => setImportWorldOpen(true)}
            >
              <FileDown className='w-4 h-4 mr-2' />
              Import world setup
            </Button>
          )}
        </div>

        <p className='text-muted-foreground'>
          Configure the world prompt, scenario, currency cost, and shared
          resources. Model, RAG limits, and preferred LLM are set on each
          advisor. Advisors that use world behavioral defaults read the text and
          currency cost from here.
        </p>

        <div className='mt-4 rounded-xl border border-slate-200 bg-white shadow-sm'>
          <div className='border-b px-4 py-3 flex items-center justify-between bg-slate-50'>
            <div>
              <h2 className='text-sm font-semibold text-slate-800'>
                World Configuration
              </h2>
              <p className='text-xs text-slate-500'>
                Shared resources and scenario used by advisors in world-default
                mode.
              </p>
            </div>
          </div>
          <div className='px-4 py-4 space-y-6'>
            <Formik
              initialValues={roomConfigInitialValues}
              enableReinitialize
              onSubmit={async (values, { setSubmitting }) => {
                await handleSaveRoomAdvisorConfig(values)
                setSubmitting(false)
              }}
            >
              {({ isSubmitting }) => (
                <Form className='space-y-6'>
                  <BehaviorSection
                    chatbotType={ChatbotType.ADVISOR}
                    isGameCompleted={isGameCompleted}
                    hideTestChatControls
                  />
                  <FileUpload
                    chatbotid={''}
                    disabled={isGameCompleted}
                    advisorRAGDefaults={ROOM_FILE_RAG_DEFAULTS}
                    uploadTarget='room'
                    roomId={roomId}
                  />
                  <div className='flex justify-end'>
                    <Button
                      type='submit'
                      className='bg-dark-blue hover:bg-dark-blue-hovered'
                      disabled={isSavingRoomConfig || isSubmitting}
                    >
                      {isSavingRoomConfig || isSubmitting
                        ? 'Saving...'
                        : 'Save World Default Advisor'}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
        {importWorldOpen && roomId && (
          <ImportModal
            key={`${roomId}-world-import`}
            isOpen={importWorldOpen}
            onClose={() => setImportWorldOpen(false)}
            scope='world_setup'
            roomId={roomId}
            roomName={currentRoom.roomname?.toString()}
            onImportComplete={() => {
              setImportWorldOpen(false)
              void dispatch(getRoomDetails(roomId))
              const loadRoomAdvisorConfig = async () => {
                try {
                  const config = await fetchRoomAdvisorConfig(roomId)
                  applyRoomAdvisorConfig(config)
                } catch (error) {
                  console.error('Failed to fetch room advisor config', error)
                }
              }
              void loadRoomAdvisorConfig()
            }}
          />
        )}
      </div>
    </TooltipProvider>
  )
}
